import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import { HelpdeskPriorityI18nEnum } from '@zaoblako/shared/modules/helpdesk/i18n/helpdesk.priority.i18n.enum';
import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { nonEmptyStringSchema } from '@zaoblako/shared/modules/serde/schemas/non-empty-string.schema';
import { commonEnumValidator } from '@zaoblako/shared/modules/serde/validators/common-enum.validator';
import { entityLikeSchema, entityLikeSchemaStrict } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { ProjectCheckersEnum } from '@zaoblako/shared/modules/project/enum/project-checkers.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { HelpdeskCheckerKeyEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk-checker-key.enum';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { MasterMilestoneFindInterface } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';

import * as yup from 'yup';

/** Project update API request body - DTO signature */
export interface ProjectFormInterface {
  /** Display name */
  name: string;
  /** Additional description (HTML supported) */
  description?: string;
  /** Deadline date */
  deadline?: Date;
  /** Priority ({@link HelpdeskTaskPriorityEnum.MEDIUM} by default) */
  priority?: HelpdeskTaskPriorityEnum;
  /** Creator user (current authorized user by default) */
  creator?: UserFindInterface;
  /** Planned estimate in minutes (zero by default) */
  plannedMin?: number;
  /** Responsible user */
  responsible: UserFindInterface;
  /** Auditor users' groups */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Auditor users */
  userAuditors?: UserFindInterface[];
  /** Parent project */
  parent?: ProjectFindInterface;
  /** Master milestone to assign */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Deals to assgin */
  deals?: DealFindInterface[];
  /** Client to assign */
  client?: ClientFindInterface;
  /** Direction to assign */
  direction?: DirectionFindInterface;
  /** Unit to assign */
  unit?: UnitFindInterface;
  /** Filial to assign */
  filial?: FilialFindInterface;
  /** HD tasks to assign */
  helpdeskTasks?: HelpdeskTaskFindInterface[];
}

/** Project update API request body - DTO schema */
export const ProjectFormDto = SerdeDtoClass.create<ProjectFormInterface>(ProjectDtoKeysEnum.PROJECT_FORM_DTO, {
  name: {
    label: 'Название',
    serializer: nonEmptyStringSchema,
  },
  description: {
    optional: true,
    label: 'Описание',
    serializer: yup.string(),
  },
  deadline: {
    optional: true,
    label: 'Дедлайн',
    serializer: yup.date(),
  },
  priority: {
    optional: true,
    default: HelpdeskTaskPriorityEnum.MEDIUM,
    label: 'Приоритет',
    serializer: commonEnumValidator(HelpdeskTaskPriorityEnum, HelpdeskPriorityI18nEnum),
  },
  creator: {
    optional: true,
    label: 'Автор',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  plannedMin: {
    optional: true,
    label: 'Время, план',
    serializer: yup.number(),
  },
  responsible: {
    label: 'Ответственный',
    serializer: entityLikeSchemaStrict,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
  userAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  parent: {
    optional: true,
    label: 'Надзадача',
    serializer: entityLikeSchema,
    checkers: [ProjectCheckersEnum.PROJECT_ASSERT_EXISTS_BY_DTO],
  },
  masterMilestone: {
    optional: true,
    label: 'Мастер-веха',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.MASTER_MILESTONE_ASSERT_EXISTS_BY_DTO],
  },
  deals: {
    optional: true,
    label: 'Сделки',
    serializer: yup.array(entityLikeSchema),
    checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
  },
  client: {
    optional: true,
    label: 'Клиент',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.CLIENT_ASSERT_EXISTS_BY_DTO],
  },
  direction: {
    optional: true,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    optional: true,
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  helpdeskTasks: {
    optional: true,
    label: 'Заявки HD',
    serializer: yup.array(entityLikeSchema),
    checkers: [HelpdeskCheckerKeyEnum.HELPDESK_TASK_ASSERT_EXISTS_BY_DTO],
  },
});
